import { IProvider } from './base';

/**
 * Provider Registry
 *
 * Central registry for managing asset providers in the Media Library.
 * Allows dynamic registration and retrieval of providers based on capabilities.
 */
export class ProviderRegistry {
  private providers: Map<string, IProvider> = new Map();

  /**
   * Register a provider with the registry
   * @param provider - The provider to register
   */
  register(provider: IProvider): void {
    this.providers.set(provider.getId(), provider);
  }

  /**
   * Unregister a provider from the registry
   * @param id - The provider ID to unregister
   * @returns True if the provider was found and removed
   */
  unregister(id: string): boolean {
    return this.providers.delete(id);
  }

  /**
   * Get a provider by ID
   * @param id - The provider ID
   * @returns The provider, or undefined if not found
   */
  get(id: string): IProvider | undefined {
    return this.providers.get(id);
  }

  /**
   * Get all registered providers
   * @returns Array of all providers
   */
  getAll(): IProvider[] {
    return Array.from(this.providers.values());
  }

  /**
   * Get all providers that support reading
   * @returns Array of providers with read capability
   */
  getReadProviders(): IProvider[] {
    return this.getAll().filter(provider => provider.canRead());
  }

  /**
   * Get all providers that support writing
   * @returns Array of providers with write capability
   */
  getWriteProviders(): IProvider[] {
    return this.getAll().filter(provider => provider.canWrite());
  }

  /**
   * Get all providers that support deletion
   * @returns Array of providers with delete capability
   */
  getDeleteProviders(): IProvider[] {
    return this.getAll().filter(provider => provider.canDelete());
  }

  /**
   * Get providers that support a specific asset type
   * @param type - The asset type to filter by
   * @returns Array of providers that support the given type
   */
  getProvidersByType(type: string): IProvider[] {
    return this.getAll().filter(provider =>
      provider.getSupportedTypes().includes(type)
    );
  }

  /**
   * Check if a provider is registered
   * @param id - The provider ID to check
   * @returns True if the provider is registered
   */
  has(id: string): boolean {
    return this.providers.has(id);
  }

  /**
   * Clear all registered providers
   */
  clear(): void {
    this.providers.clear();
  }

  /**
   * Get the number of registered providers
   * @returns The count of registered providers
   */
  get size(): number {
    return this.providers.size;
  }
}

/**
 * Default global provider registry instance
 * Use this for simple cases where you don't need multiple registries
 */
export const defaultRegistry = new ProviderRegistry();
